import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReplayIcon from "@mui/icons-material/Replay";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import FinancialCoreApi from "../../../../infrastructure/api/financialCore";
import LendingOperationsApi from "../../../../infrastructure/api/lendingOperations";
import OperatorInvoiceIssueDialog from "./OperatorInvoiceIssueDialog";
import ManagementPaymentGatewayApi from "../../../../infrastructure/api/managementPaymentGateway";
import PaymentStatusBadge from "../../payment-gateway/PaymentStatusBadge";
import {
  mapSessionToBadgeState,
  pick as pickGatewayField,
} from "../../payment-gateway/paymentGatewayUtils";
import { toJalaliFullDate } from "../../../middleware/middleware";
import JalaliDatePicker from "../../shared/jalali-datepicker";
import FileUploader from "../../shared/uploader/FileUploader";
import MehrsanLogAttachment from "./MehrsanLogAttachment";
import {
  PAYMENT_METHOD_LEGACY,
  PAYMENT_METHOD_LOAN,
  PAYMENT_METHOD_ONLINE,
  PAYMENT_METHODS,
  STATUS_WAITING_APPROVAL,
  STATUS_REJECTED,
  STATUS_SUCCEEDED,
  getFinancialStatusChipColor,
  getInvoiceStatusChipColor,
  getTransactionStatusChipColor,
} from "./financialCoreConstants";
import {
  formatMoney,
  getFinancialStatusLabel,
  getInvoiceStatusLabel,
  getPaymentMethodLabel,
  getTransactionStatusLabel,
  normalizeList,
  normalizeProfile,
  parseAmountInput,
  parseApiErrorMessage,
  pickField,
} from "./financialCoreUtils";
import { resolveOperationsSource } from "./mehrsanOperationsConstants";

const SummarySkeleton = () => (
  <Grid container spacing={1}>
    {[1, 2, 3, 4, 5, 6].map((k) => (
      <Grid item xs={6} sm={4} md={2} key={k}>
        <Skeleton variant="rounded" height={56} />
      </Grid>
    ))}
  </Grid>
);

const SummaryCard = ({ label, value, emphasis }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.25,
      borderRadius: 1.5,
      height: "100%",
      ...(emphasis === "success" && { borderColor: "success.light" }),
      ...(emphasis === "warning" && { borderColor: "warning.light" }),
      ...(emphasis === "error" && { borderColor: "error.light" }),
    }}
  >
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 0.25 }}>
      {value}
    </Typography>
  </Paper>
);

const FinancialCoreTab = ({
  requestInfoId,
  detailApi,
  fileBaseUrl = "",
  active = true,
}) => {
  const operationsSource = resolveOperationsSource(detailApi);
  const lastLoadedRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [error, setError] = useState(null);
  const [unauthorized, setUnauthorized] = useState(false);
  const [profile, setProfile] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [gatewaySummaries, setGatewaySummaries] = useState([]);
  const [gatewayActionId, setGatewayActionId] = useState(null);
  const [issuanceContext, setIssuanceContext] = useState(null);

  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [operatorInvoiceOpen, setOperatorInvoiceOpen] = useState(false);
  const [invoiceSubmitting, setInvoiceSubmitting] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({
    title: "فاکتور پرونده",
    totalAmount: "",
    dueDate: "",
    description: "",
    items: [],
  });

  const [manualForm, setManualForm] = useState({
    amount: "",
    paymentMethod: 2,
    trackingNumber: "",
    description: "",
    receiptFileUrl: "",
  });
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [loanProcesses, setLoanProcesses] = useState([]);
  const [loanForm, setLoanForm] = useState({
    loanProcessId: "",
    amount: "",
    trackingNumber: "",
    description: "",
  });
  const [loanSubmitting, setLoanSubmitting] = useState(false);
  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);
  const [approveTargetId, setApproveTargetId] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectTargetId, setRejectTargetId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [actionTxId, setActionTxId] = useState(null);
  const [rejectSubmitting, setRejectSubmitting] = useState(false);

  const loadFinancial = useCallback(
    async (force = false) => {
      if (!requestInfoId) return;
      const key = String(requestInfoId);
      if (!force && lastLoadedRef.current === key) return;

      setLoading(true);
      setError(null);
      setUnauthorized(false);

      try {
        const [profileRes, invRes, txRes, gwRes, issuanceRes, loanRes] =
          await Promise.all([
          FinancialCoreApi.getByRequestId(operationsSource, requestInfoId),
          FinancialCoreApi.getInvoicesByRequestId(operationsSource, requestInfoId),
          FinancialCoreApi.getTransactionsByRequestId(
            operationsSource,
            requestInfoId
          ),
          ManagementPaymentGatewayApi.getGatewaySummaries(
            operationsSource,
            requestInfoId
          ).catch(() => null),
          FinancialCoreApi.getInvoiceIssuanceContext(operationsSource, requestInfoId).catch(
            () => null
          ),
          LendingOperationsApi.getByRequestId(operationsSource, requestInfoId).catch(
            () => null
          ),
        ]);

        if (
          profileRes?.statusCode === "UnAuthorized" ||
          (profileRes?.isSuccess === false &&
            String(profileRes?.message || "").includes("دسترسی"))
        ) {
          setUnauthorized(true);
          return;
        }

        const { profile: p, error: pErr } = normalizeProfile(profileRes);
        if (pErr && profileRes?.isSuccess === false) {
          setError(parseApiErrorMessage(profileRes, "بارگذاری پروفایل مالی ناموفق بود."));
        } else {
          setProfile(p);
        }

        if (invRes?.isSuccess === false) {
          setInvoices([]);
        } else {
          setInvoices(normalizeList(invRes));
        }

        if (txRes?.isSuccess === false) {
          setTransactions([]);
        } else {
          setTransactions(normalizeList(txRes));
        }

        if (gwRes?.isSuccess !== false) {
          setGatewaySummaries(normalizeList(gwRes));
        } else {
          setGatewaySummaries([]);
        }

        if (issuanceRes?.isSuccess !== false && issuanceRes?.data) {
          setIssuanceContext(issuanceRes.data);
        } else {
          setIssuanceContext(null);
        }

        const loans = normalizeList(loanRes);
        setLoanProcesses(loans);
        if (loans.length > 0) {
          const firstId = pickField(loans[0], "id", "Id");
          const approved =
            pickField(loans[0], "approvedAmount", "ApprovedAmount") ??
            pickField(loans[0], "requestedAmount", "RequestedAmount") ??
            "";
          setLoanForm((f) => ({
            ...f,
            loanProcessId: f.loanProcessId || (firstId != null ? String(firstId) : ""),
            amount: f.amount || (approved !== "" && approved != null ? String(approved) : ""),
          }));
        }

        lastLoadedRef.current = key;
      } catch {
        setError("بارگذاری اطلاعات مالی ناموفق بود.");
      } finally {
        setLoading(false);
      }
    },
    [operationsSource, requestInfoId]
  );

  const refreshFinancial = useCallback(() => {
    lastLoadedRef.current = null;
    loadFinancial(true);
  }, [loadFinancial]);

  const findGatewaySummary = useCallback(
    (tx) => {
      const txId = pickField(tx, "id", "Id");
      const invoiceId = pickField(tx, "invoiceId", "InvoiceId");
      return gatewaySummaries.find(
        (g) =>
          pickGatewayField(g, "transactionId", "TransactionId") === txId ||
          (invoiceId &&
            pickGatewayField(g, "invoiceId", "InvoiceId") === invoiceId)
      );
    },
    [gatewaySummaries]
  );

  const handleResumeGateway = async (sessionId) => {
    if (!sessionId) return;
    setGatewayActionId(sessionId);
    try {
      const res = await ManagementPaymentGatewayApi.resumeSession(
        operationsSource,
        sessionId
      );
      if (res?.isSuccess === false) {
        toast.error(parseApiErrorMessage(res, "ادامه پردازش جلسه ناموفق بود."));
        return;
      }
      toast.success(res?.message || "جلسه پرداخت بررسی شد.");
      refreshFinancial();
    } catch {
      toast.error("خطا در ادامه پردازش جلسه پرداخت");
    } finally {
      setGatewayActionId(null);
    }
  };

  const handleRecalculate = async () => {
    if (!requestInfoId) return;
    setRecalculating(true);
    try {
      const res = await FinancialCoreApi.recalculateProfile(
        operationsSource,
        requestInfoId
      );
      if (res?.isSuccess === false) {
        toast.error(parseApiErrorMessage(res, "بروزرسانی مالی ناموفق بود."));
        return;
      }
      toast.success(res?.message || "پروفایل مالی به‌روزرسانی شد");
      lastLoadedRef.current = null;
      await loadFinancial(true);
    } catch {
      toast.error("بروزرسانی مالی ناموفق بود.");
    } finally {
      setRecalculating(false);
    }
  };

  const handleCreateInvoice = async () => {
    const amount = parseAmountInput(invoiceForm.totalAmount);
    const items = (invoiceForm.items || [])
      .filter((row) => row && (row.unitPrice || row.title))
      .map((row) => ({
        title: row.title || "قلم",
        description: row.description || invoiceForm.description || null,
        quantity: parseAmountInput(row.quantity) ?? 1,
        unitPrice: parseAmountInput(row.unitPrice) ?? 0,
      }));

    setInvoiceSubmitting(true);
    try {
      const res = await FinancialCoreApi.createInvoice(operationsSource, {
        requestInfoId: Number(requestInfoId),
        title: invoiceForm.title,
        totalAmount: amount,
        dueDate: invoiceForm.dueDate || null,
        items: items.length ? items : null,
      });
      if (res?.isSuccess === false) {
        toast.error(parseApiErrorMessage(res, "صدور فاکتور ناموفق بود."));
        return;
      }
      toast.success(res?.message || "فاکتور ایجاد شد");
      setInvoiceOpen(false);
      refreshFinancial();
    } catch {
      toast.error("صدور فاکتور ناموفق بود.");
    } finally {
      setInvoiceSubmitting(false);
    }
  };

  const handleRegisterManual = async () => {
    const amount = parseAmountInput(manualForm.amount);
    if (!amount || amount <= 0) {
      toast.warning("مبلغ را وارد کنید.");
      return;
    }

    setManualSubmitting(true);
    try {
      const res = await FinancialCoreApi.registerManualTransaction(operationsSource, {
        requestInfoId: Number(requestInfoId),
        amount,
        paymentMethod: Number(manualForm.paymentMethod),
        paymentStatus: STATUS_WAITING_APPROVAL,
        trackingNumber: manualForm.trackingNumber || null,
        receiptFileUrl: manualForm.receiptFileUrl || null,
        description: manualForm.description || null,
      });
      if (res?.isSuccess === false) {
        toast.error(parseApiErrorMessage(res, "ثبت پرداخت ناموفق بود."));
        return;
      }
      const txData = res?.data ?? res?.Data;
      const duplicateWarning =
        txData?.duplicateWarning ?? txData?.DuplicateWarning;
      if (duplicateWarning) {
        toast.warning(duplicateWarning);
      }
      toast.success(res?.message || "تراکنش ثبت شد و در انتظار تایید است");
      setManualForm({
        amount: "",
        paymentMethod: 2,
        trackingNumber: "",
        description: "",
        receiptFileUrl: "",
      });
      refreshFinancial();
    } catch {
      toast.error("ثبت پرداخت ناموفق بود.");
    } finally {
      setManualSubmitting(false);
    }
  };

  const handleRegisterLoanDisbursement = async () => {
    const amount = parseAmountInput(loanForm.amount);
    if (!amount || amount <= 0) {
      toast.warning("مبلغ واریز وام را وارد کنید.");
      return;
    }
    const tracking = (loanForm.trackingNumber || "").trim();
    if (!tracking) {
      toast.warning("شماره سند واریز را وارد کنید.");
      return;
    }

    setLoanSubmitting(true);
    try {
      const res = await FinancialCoreApi.registerLoanDisbursementTransaction(
        operationsSource,
        {
          requestInfoId: Number(requestInfoId),
          loanProcessId: loanForm.loanProcessId
            ? Number(loanForm.loanProcessId)
            : 0,
          amount,
          trackingNumber: tracking,
          description: loanForm.description?.trim() || null,
        }
      );
      if (res?.isSuccess === false) {
        toast.error(parseApiErrorMessage(res, "ثبت واریز وام ناموفق بود."));
        return;
      }
      toast.success(res?.message || "واریز وام ثبت شد");
      setLoanForm((f) => ({
        ...f,
        trackingNumber: "",
        description: "",
      }));
      refreshFinancial();
    } catch {
      toast.error("ثبت واریز وام ناموفق بود.");
    } finally {
      setLoanSubmitting(false);
    }
  };

  useEffect(() => {
    if (!active || !requestInfoId) return;
    const key = String(requestInfoId);
    if (lastLoadedRef.current !== key) {
      setError(null);
      setProfile(null);
      setInvoices([]);
      setTransactions([]);
    }
    loadFinancial();
  }, [active, requestInfoId, loadFinancial]);

  const financialStatus = Number(
    pickField(profile, "financialStatus", "FinancialStatus") ?? 0
  );
  const statusColor = getFinancialStatusChipColor(financialStatus);

  const nonLoanTransactions = useMemo(
    () =>
      transactions.filter(
        (t) =>
          Number(pickField(t, "paymentMethod", "PaymentMethod")) !==
          PAYMENT_METHOD_LOAN
      ),
    [transactions]
  );

  const isPendingApproval = (txStatus) =>
    txStatus === STATUS_WAITING_APPROVAL || txStatus === 0;

  const openApproveConfirm = (txId) => {
    setApproveTargetId(txId);
    setApproveConfirmOpen(true);
  };

  const handleApprove = async () => {
    if (!approveTargetId) return;
    setActionTxId(approveTargetId);
    setApproveConfirmOpen(false);
    try {
      const res = await FinancialCoreApi.approveTransaction(
        operationsSource,
        approveTargetId
      );
      if (res?.isSuccess === false) {
        toast.error(parseApiErrorMessage(res, "تایید تراکنش ناموفق بود."));
        return;
      }
      toast.success(res?.message || "تراکنش تایید شد");
      refreshFinancial();
    } catch {
      toast.error("تایید تراکنش ناموفق بود.");
    } finally {
      setActionTxId(null);
      setApproveTargetId(null);
    }
  };

  const openRejectDialog = (txId) => {
    setRejectTargetId(txId);
    setRejectReason("");
    setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    if (!rejectTargetId) return;
    setActionTxId(rejectTargetId);
    setRejectSubmitting(true);
    try {
      const res = await FinancialCoreApi.rejectTransaction(
        operationsSource,
        rejectTargetId,
        {
          approvalDescription: rejectReason?.trim() || null,
        }
      );
      if (res?.isSuccess === false) {
        toast.error(parseApiErrorMessage(res, "رد تراکنش ناموفق بود."));
        return;
      }
      toast.success(res?.message || "تراکنش رد شد");
      setRejectDialogOpen(false);
      setRejectTargetId(null);
      setRejectReason("");
      refreshFinancial();
    } catch {
      toast.error("رد تراکنش ناموفق بود.");
    } finally {
      setRejectSubmitting(false);
      setActionTxId(null);
    }
  };

  const loanTransactions = useMemo(
    () =>
      transactions.filter(
        (t) =>
          Number(pickField(t, "paymentMethod", "PaymentMethod")) ===
          PAYMENT_METHOD_LOAN
      ),
    [transactions]
  );

  if (unauthorized) {
    return (
      <Alert severity="warning">دسترسی به اطلاعات مالی این پرونده مجاز نیست.</Alert>
    );
  }

  if (loading && !profile && !error) {
    return (
      <Stack spacing={2}>
        <SummarySkeleton />
        <Skeleton variant="rounded" height={120} />
        <Skeleton variant="rounded" height={160} />
      </Stack>
    );
  }

  if (error && !profile) {
    return (
      <Stack spacing={2} alignItems="flex-start">
        <Alert severity="error">{error}</Alert>
        <Button
          variant="outlined"
          startIcon={<ReplayIcon />}
          onClick={() => refreshFinancial()}
        >
          تلاش مجدد
        </Button>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="subtitle2" fontWeight={700}>
            وضعیت مالی
          </Typography>
          {profile ? (
            <Chip
              size="small"
              label={getFinancialStatusLabel(
                financialStatus,
                pickField(profile, "financialStatusTitle", "FinancialStatusTitle")
              )}
              color={statusColor}
            />
          ) : null}
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={
              recalculating ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <RefreshIcon />
              )
            }
            onClick={handleRecalculate}
            disabled={recalculating}
          >
            بروزرسانی مالی
          </Button>
          <IconButton
            size="small"
            aria-label="بارگذاری مجدد"
            onClick={refreshFinancial}
            disabled={loading}
          >
            <ReplayIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      {issuanceContext ? (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 1.5 }}>
          <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
            <Chip
              size="small"
              label={`مبلغ قرارداد مشتری: ${formatMoney(
                pickField(issuanceContext, "contractAmount", "ContractAmount")
              )}`}
              color="primary"
              variant="outlined"
            />
            <Chip
              size="small"
              label={`مبلغ قابل فاکتور: ${formatMoney(
                pickField(issuanceContext, "invoiceableAmount", "InvoiceableAmount")
              )}`}
              color="secondary"
              variant="outlined"
            />
            {(pickField(issuanceContext, "loanGate", "LoanGate")?.loanGateRequired ||
              pickField(issuanceContext, "loanGate", "LoanGate")?.LoanGateRequired) &&
            !pickField(issuanceContext, "canIssueInvoice", "CanIssueInvoice") ? (
              <Chip
                size="small"
                color="warning"
                label={
                  pickField(issuanceContext, "issueBlockReason", "IssueBlockReason") ||
                  "در انتظار واریز وام"
                }
              />
            ) : null}
          </Stack>
        </Paper>
      ) : null}

      {!profile ? (
        <Alert severity="info">
          پروفایل مالی هنوز ایجاد نشده است. «بروزرسانی مالی» را بزنید.
        </Alert>
      ) : (
        <Grid container spacing={1}>
          <Grid item xs={6} sm={4} md={2}>
            <SummaryCard
              label="مبلغ قرارداد مشتری"
              value={formatMoney(
                pickField(issuanceContext, "contractAmount", "ContractAmount") ??
                  pickField(profile, "agreedProjectAmount", "AgreedProjectAmount")
              )}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <SummaryCard
              label="وام تأییدشده"
              value={formatMoney(
                pickField(profile, "approvedLoanAmount", "ApprovedLoanAmount")
              )}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <SummaryCard
              label="قابل پرداخت مشتری"
              value={formatMoney(
                pickField(profile, "customerPayableAmount", "CustomerPayableAmount")
              )}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <SummaryCard
              label="پرداخت‌شده"
              value={formatMoney(pickField(profile, "paidAmount", "PaidAmount"))}
              emphasis="success"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <SummaryCard
              label="مانده"
              value={formatMoney(
                pickField(profile, "remainingAmount", "RemainingAmount")
              )}
              emphasis={statusColor === "error" ? "error" : undefined}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <SummaryCard
              label="آخرین محاسبه"
              value={
                pickField(profile, "lastCalculatedAt", "LastCalculatedAt")
                  ? toJalaliFullDate(
                      pickField(profile, "lastCalculatedAt", "LastCalculatedAt")
                    )
                  : "—"
              }
            />
          </Grid>
        </Grid>
      )}

      <Accordion defaultExpanded disableGutters elevation={0} sx={{ border: 1, borderColor: "divider", borderRadius: 1.5, "&:before": { display: "none" } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            pr={1}
          >
            <Typography variant="subtitle2" fontWeight={700}>
              فاکتورها
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              disabled={issuanceContext && !pickField(issuanceContext, "canIssueInvoice", "CanIssueInvoice")}
              title={
                issuanceContext && !pickField(issuanceContext, "canIssueInvoice", "CanIssueInvoice")
                  ? pickField(issuanceContext, "issueBlockReason", "IssueBlockReason") || ""
                  : ""
              }
              onClick={(e) => {
                e.stopPropagation();
                if (
                  issuanceContext &&
                  !pickField(issuanceContext, "canIssueInvoice", "CanIssueInvoice")
                ) {
                  toast.warning(
                    pickField(issuanceContext, "issueBlockReason", "IssueBlockReason") ||
                      "صدور فاکتور مجاز نیست."
                  );
                  return;
                }
                const suggested = pickField(
                  issuanceContext,
                  "invoiceableAmount",
                  "InvoiceableAmount"
                );
                if (suggested) {
                  setInvoiceForm((f) => ({
                    ...f,
                    totalAmount: String(suggested),
                  }));
                }
                setInvoiceOpen(true);
              }}
            >
              صدور فاکتور
            </Button>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          {invoices.length === 0 ? (
            <Stack spacing={1}>
              <Alert severity="info">
                پرداخت مشتری فقط پس از صدور فاکتور فعال می‌شود. تا زمان صدور فاکتور،
                اقدام پرداخت آنلاین نمایش داده نمی‌شود.
              </Alert>
              <Typography variant="body2" color="text.secondary">
                فاکتوری ثبت نشده است.
              </Typography>
            </Stack>
          ) : (
            <TableContainer sx={{ maxWidth: "100%" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>شماره</TableCell>
                    <TableCell>عنوان</TableCell>
                    <TableCell align="left">مبلغ</TableCell>
                    <TableCell align="left">پرداخت</TableCell>
                    <TableCell align="left">مانده</TableCell>
                    <TableCell>وضعیت</TableCell>
                    <TableCell>تاریخ</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map((inv) => {
                    const invStatus = Number(
                      pickField(inv, "invoiceStatus", "InvoiceStatus")
                    );
                    return (
                      <TableRow key={pickField(inv, "id", "Id")}>
                        <TableCell>
                          {pickField(inv, "invoiceNumber", "InvoiceNumber")}
                        </TableCell>
                        <TableCell>{pickField(inv, "title", "Title")}</TableCell>
                        <TableCell align="left">
                          {formatMoney(pickField(inv, "totalAmount", "TotalAmount"))}
                        </TableCell>
                        <TableCell align="left">
                          {formatMoney(pickField(inv, "paidAmount", "PaidAmount"))}
                        </TableCell>
                        <TableCell align="left">
                          {formatMoney(
                            pickField(inv, "remainingAmount", "RemainingAmount")
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={getInvoiceStatusLabel(
                              invStatus,
                              pickField(inv, "invoiceStatusTitle", "InvoiceStatusTitle")
                            )}
                            color={getInvoiceStatusChipColor(invStatus)}
                          />
                        </TableCell>
                        <TableCell>
                          {pickField(inv, "issuedAt", "IssuedAt")
                            ? toJalaliFullDate(pickField(inv, "issuedAt", "IssuedAt"))
                            : "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded disableGutters elevation={0} sx={{ border: 1, borderColor: "divider", borderRadius: 1.5, "&:before": { display: "none" } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" fontWeight={700}>
            تراکنش‌ها
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          {nonLoanTransactions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              تراکنشی ثبت نشده است.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {nonLoanTransactions.map((tx) => {
                const method = Number(
                  pickField(tx, "paymentMethod", "PaymentMethod")
                );
                const txStatus = Number(
                  pickField(tx, "paymentStatus", "PaymentStatus")
                );
                const receipt = pickField(tx, "receiptFileUrl", "ReceiptFileUrl");
                const isLegacy = method === PAYMENT_METHOD_LEGACY;
                const isOnline = method === PAYMENT_METHOD_ONLINE;
                const gatewaySummary = isOnline ? findGatewaySummary(tx) : null;
                const gatewaySessionId = pickGatewayField(
                  gatewaySummary,
                  "sessionId",
                  "SessionId"
                );
                const gatewayStatus = pickGatewayField(
                  gatewaySummary,
                  "sessionStatus",
                  "SessionStatus"
                );
                const txId = pickField(tx, "id", "Id");
                const pending = isPendingApproval(txStatus);
                const rejected = txStatus === STATUS_REJECTED;
                const rejectReason = pickField(
                  tx,
                  "approvalDescription",
                  "ApprovalDescription"
                );

                return (
                  <Paper key={txId} variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      spacing={1}
                    >
                      <Box>
                        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                          <Typography variant="subtitle2" fontWeight={700}>
                            {formatMoney(pickField(tx, "amount", "Amount"))}
                          </Typography>
                          {isLegacy ? (
                            <Chip size="small" label="Legacy" color="default" variant="outlined" />
                          ) : null}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {getPaymentMethodLabel(
                            method,
                            pickField(tx, "paymentMethodTitle", "PaymentMethodTitle")
                          )}
                        </Typography>
                        {pickField(tx, "trackingNumber", "TrackingNumber") ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            پیگیری: {pickField(tx, "trackingNumber", "TrackingNumber")}
                          </Typography>
                        ) : null}
                        {isOnline && pickField(tx, "providerTrackingCode", "ProviderTrackingCode") ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            پیگیری درگاه:{" "}
                            {pickField(tx, "providerTrackingCode", "ProviderTrackingCode")}
                          </Typography>
                        ) : null}
                        {isOnline && gatewaySummary ? (
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                            <PaymentStatusBadge
                              state={mapSessionToBadgeState(gatewayStatus)}
                              label={
                                pickGatewayField(
                                  gatewaySummary,
                                  "sessionStatusTitle",
                                  "SessionStatusTitle"
                                ) || undefined
                              }
                            />
                            <Typography variant="caption" color="text.secondary">
                              {pickGatewayField(
                                gatewaySummary,
                                "providerTitle",
                                "ProviderTitle"
                              ) || "درگاه آنلاین"}
                            </Typography>
                          </Stack>
                        ) : null}
                        {pickField(tx, "description", "Description") ? (
                          <Typography variant="caption" display="block" sx={{ mt: 0.25 }}>
                            {pickField(tx, "description", "Description")}
                          </Typography>
                        ) : null}
                      </Box>
                      <Stack alignItems={{ xs: "flex-start", sm: "flex-end" }} spacing={0.5}>
                        <Chip
                          size="small"
                          label={
                            pending
                              ? "در انتظار تایید"
                              : txStatus === STATUS_SUCCEEDED
                                ? "تایید شده"
                                : rejected
                                  ? "رد شده"
                                  : getTransactionStatusLabel(
                                      txStatus,
                                      pickField(
                                        tx,
                                        "paymentStatusTitle",
                                        "PaymentStatusTitle"
                                      )
                                    )
                          }
                          color={getTransactionStatusChipColor(txStatus)}
                        />
                        {rejected && rejectReason ? (
                          <Typography variant="caption" color="error.main" display="block">
                            {rejectReason}
                          </Typography>
                        ) : null}
                        <Typography variant="caption" color="text.secondary">
                          {pickField(tx, "paidAt", "PaidAt")
                            ? toJalaliFullDate(pickField(tx, "paidAt", "PaidAt"))
                            : pickField(tx, "createOn", "CreateOn")
                              ? toJalaliFullDate(pickField(tx, "createOn", "CreateOn"))
                              : "—"}
                        </Typography>
                      </Stack>
                    </Stack>
                    {pending ? (
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={
                            actionTxId === txId ? (
                              <CircularProgress size={14} color="inherit" />
                            ) : (
                              <CheckIcon />
                            )
                          }
                          disabled={actionTxId != null}
                          onClick={() => openApproveConfirm(txId)}
                        >
                          تایید
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<CloseIcon />}
                          disabled={actionTxId != null}
                          onClick={() => openRejectDialog(txId)}
                        >
                          رد
                        </Button>
                      </Stack>
                    ) : null}
                    {isOnline &&
                    pickGatewayField(gatewaySummary, "canResume", "CanResume") ? (
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        sx={{ mt: 1 }}
                        startIcon={
                          gatewayActionId === gatewaySessionId ? (
                            <CircularProgress size={14} />
                          ) : (
                            <ReplayIcon />
                          )
                        }
                        disabled={gatewayActionId != null}
                        onClick={() => handleResumeGateway(gatewaySessionId)}
                      >
                        بررسی مجدد پرداخت
                      </Button>
                    ) : null}
                    {receipt ? (
                      <Box sx={{ mt: 1 }}>
                        <MehrsanLogAttachment url={receipt} fileBaseUrl={fileBaseUrl} />
                      </Box>
                    ) : null}
                  </Paper>
                );
              })}
            </Stack>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters elevation={0} sx={{ border: 1, borderColor: "divider", borderRadius: 1.5, "&:before": { display: "none" } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" fontWeight={700}>
            ثبت پرداخت
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={1.5} sx={{ maxWidth: 480 }}>
            <TextField
              label="مبلغ (ریال)"
              size="small"
              fullWidth
              value={manualForm.amount}
              onChange={(e) =>
                setManualForm((f) => ({ ...f, amount: e.target.value }))
              }
            />
            <FormControl size="small" fullWidth>
              <InputLabel id="fc-pay-method">روش پرداخت</InputLabel>
              <Select
                labelId="fc-pay-method"
                label="روش پرداخت"
                value={manualForm.paymentMethod}
                onChange={(e) =>
                  setManualForm((f) => ({
                    ...f,
                    paymentMethod: e.target.value,
                  }))
                }
              >
                {PAYMENT_METHODS.filter((m) => m.value !== PAYMENT_METHOD_LEGACY && m.value !== PAYMENT_METHOD_LOAN).map(
                  (m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
            <TextField
              label="شماره پیگیری"
              size="small"
              fullWidth
              value={manualForm.trackingNumber}
              onChange={(e) =>
                setManualForm((f) => ({ ...f, trackingNumber: e.target.value }))
              }
            />
            <TextField
              label="توضیح"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={manualForm.description}
              onChange={(e) =>
                setManualForm((f) => ({ ...f, description: e.target.value }))
              }
            />
            <FileUploader
              moduleName="SLN"
              label="فایل رسید"
              accept="image/*,application/pdf"
              maxFiles={1}
              baseUrl={fileBaseUrl}
              value={manualForm.receiptFileUrl}
              onChange={(url) =>
                setManualForm((f) => ({
                  ...f,
                  receiptFileUrl: Array.isArray(url) ? url[0] : url,
                }))
              }
            />
            <Button
              variant="contained"
              disabled={manualSubmitting}
              onClick={handleRegisterManual}
            >
              {manualSubmitting ? "در حال ثبت…" : "ثبت پرداخت"}
            </Button>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters elevation={0} sx={{ border: 1, borderColor: "divider", borderRadius: 1.5, "&:before": { display: "none" } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" fontWeight={700}>
            وام‌ها (تراکنش پرداخت وام)
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Stack spacing={1.5} sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              ثبت تکی واریز وام برای این پرونده (مثلاً سهم یک متقاضی از واریز تجمیعی).
            </Typography>
            {loanProcesses.length > 1 ? (
              <FormControl size="small" fullWidth>
                <InputLabel id="fc-loan-process">فرآیند وام</InputLabel>
                <Select
                  labelId="fc-loan-process"
                  label="فرآیند وام"
                  value={loanForm.loanProcessId}
                  onChange={(e) =>
                    setLoanForm((f) => ({
                      ...f,
                      loanProcessId: e.target.value,
                    }))
                  }
                >
                  {loanProcesses.map((lp) => {
                    const id = pickField(lp, "id", "Id");
                    const title =
                      pickField(lp, "title", "Title") ||
                      pickField(lp, "partnerTitle", "PartnerTitle") ||
                      `فرآیند #${id}`;
                    return (
                      <MenuItem key={id} value={String(id)}>
                        {title}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            ) : null}
            <TextField
              label="مبلغ واریزی (ریال)"
              size="small"
              fullWidth
              value={loanForm.amount}
              onChange={(e) =>
                setLoanForm((f) => ({ ...f, amount: e.target.value }))
              }
            />
            <TextField
              label="شماره سند"
              size="small"
              fullWidth
              value={loanForm.trackingNumber}
              onChange={(e) =>
                setLoanForm((f) => ({ ...f, trackingNumber: e.target.value }))
              }
            />
            <TextField
              label="توضیح"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={loanForm.description}
              onChange={(e) =>
                setLoanForm((f) => ({ ...f, description: e.target.value }))
              }
            />
            <Button
              variant="contained"
              color="success"
              disabled={loanSubmitting || loanProcesses.length === 0}
              onClick={handleRegisterLoanDisbursement}
            >
              {loanSubmitting
                ? "در حال ثبت…"
                : loanProcesses.length === 0
                  ? "فرآیند وام وجود ندارد"
                  : "ثبت واریز تکی وام"}
            </Button>
          </Stack>
          <Divider sx={{ mb: 1.5 }} />
          {loanTransactions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              تراکنش وام ثبت نشده است.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {loanTransactions.map((tx) => {
                const txStatus = Number(
                  pickField(tx, "paymentStatus", "PaymentStatus")
                );
                return (
                  <Paper
                    key={pickField(tx, "id", "Id")}
                    variant="outlined"
                    sx={{ p: 1.25, borderRadius: 1.5 }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      flexWrap="wrap"
                      gap={1}
                    >
                      <Box>
                        <Typography variant="subtitle2" fontWeight={700}>
                          {formatMoney(pickField(tx, "amount", "Amount"))}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          لندتک / وام
                          {pickField(tx, "loanProcessId", "LoanProcessId")
                            ? ` — فرآیند #${pickField(tx, "loanProcessId", "LoanProcessId")}`
                            : ""}
                          {pickField(tx, "trackingNumber", "TrackingNumber")
                            ? ` — سند ${pickField(tx, "trackingNumber", "TrackingNumber")}`
                            : ""}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={getTransactionStatusLabel(
                          txStatus,
                          pickField(tx, "paymentStatusTitle", "PaymentStatusTitle")
                        )}
                        color={getTransactionStatusChipColor(txStatus)}
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {pickField(tx, "paidAt", "PaidAt")
                        ? toJalaliFullDate(pickField(tx, "paidAt", "PaidAt"))
                        : "—"}
                    </Typography>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </AccordionDetails>
      </Accordion>

      <OperatorInvoiceIssueDialog
        open={operatorInvoiceOpen}
        onClose={() => setOperatorInvoiceOpen(false)}
        operationsSource={operationsSource}
        requestInfoId={requestInfoId}
        customerInfoId={
          invoices[0]
            ? pickField(invoices[0], "customerInfoId", "CustomerInfoId")
            : 0
        }
        onCreated={refreshFinancial}
      />

      <Dialog open={invoiceOpen} onClose={() => setInvoiceOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>صدور فاکتور</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <TextField
              label="عنوان"
              size="small"
              fullWidth
              value={invoiceForm.title}
              onChange={(e) =>
                setInvoiceForm((f) => ({ ...f, title: e.target.value }))
              }
            />
            <TextField
              label="مبلغ (ریال) — اختیاری اگر اقلام دارید"
              size="small"
              fullWidth
              value={invoiceForm.totalAmount}
              onChange={(e) =>
                setInvoiceForm((f) => ({ ...f, totalAmount: e.target.value }))
              }
            />
            <JalaliDatePicker
              label="سررسید"
              value={invoiceForm.dueDate}
              onChange={(v) => setInvoiceForm((f) => ({ ...f, dueDate: v }))}
            />
            <TextField
              label="توضیح"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={invoiceForm.description}
              onChange={(e) =>
                setInvoiceForm((f) => ({ ...f, description: e.target.value }))
              }
            />
            <Divider />
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary">
                اقلام (اختیاری)
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() =>
                  setInvoiceForm((f) => ({
                    ...f,
                    items: [
                      ...(f.items || []),
                      { title: "", quantity: "1", unitPrice: "" },
                    ],
                  }))
                }
              >
                افزودن قلم
              </Button>
            </Stack>
            {(invoiceForm.items || []).map((row, idx) => (
              <Stack key={idx} direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  label="عنوان قلم"
                  size="small"
                  sx={{ flex: 2 }}
                  value={row.title}
                  onChange={(e) => {
                    const items = [...invoiceForm.items];
                    items[idx] = { ...items[idx], title: e.target.value };
                    setInvoiceForm((f) => ({ ...f, items }));
                  }}
                />
                <TextField
                  label="تعداد"
                  size="small"
                  sx={{ flex: 1 }}
                  value={row.quantity}
                  onChange={(e) => {
                    const items = [...invoiceForm.items];
                    items[idx] = { ...items[idx], quantity: e.target.value };
                    setInvoiceForm((f) => ({ ...f, items }));
                  }}
                />
                <TextField
                  label="فی"
                  size="small"
                  sx={{ flex: 1 }}
                  value={row.unitPrice}
                  onChange={(e) => {
                    const items = [...invoiceForm.items];
                    items[idx] = { ...items[idx], unitPrice: e.target.value };
                    setInvoiceForm((f) => ({ ...f, items }));
                  }}
                />
              </Stack>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvoiceOpen(false)}>انصراف</Button>
          <Button
            variant="contained"
            disabled={invoiceSubmitting}
            onClick={handleCreateInvoice}
          >
            {invoiceSubmitting ? "در حال ثبت…" : "ثبت فاکتور"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={approveConfirmOpen}
        onClose={() => !actionTxId && setApproveConfirmOpen(false)}
      >
        <DialogTitle>تایید تراکنش</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            این تراکنش پس از تایید در مبلغ پرداخت‌شده لحاظ می‌شود. ادامه می‌دهید؟
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setApproveConfirmOpen(false)}
            disabled={actionTxId != null}
          >
            انصراف
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApprove}
            disabled={actionTxId != null}
          >
            تایید
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rejectDialogOpen}
        onClose={() => !rejectSubmitting && setRejectDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>رد تراکنش</DialogTitle>
        <DialogContent>
          <TextField
            label="دلیل رد (اختیاری)"
            size="small"
            fullWidth
            multiline
            minRows={2}
            sx={{ mt: 0.5 }}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRejectDialogOpen(false)}
            disabled={rejectSubmitting}
          >
            انصراف
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={rejectSubmitting}
          >
            {rejectSubmitting ? "در حال رد…" : "رد تراکنش"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

FinancialCoreTab.propTypes = {
  requestInfoId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  detailApi: PropTypes.string,
  fileBaseUrl: PropTypes.string,
  active: PropTypes.bool,
};

export default FinancialCoreTab;
